/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.RestClient
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.model.Assessment
import kotlin.time.Duration.Companion.days

/*
 * Manages creating a new assessment (or resuming where the user previously left off).
 *
 * The actual flow is:
 *  1. List recent assessments belonging to the user
 *  2. Get the status of the most recent assessment
 *  3. Give user the option to resume that assessment or start a new one
 */
class AssessmentViewModel(
    val authenticatedUser: AuthenticatedUser,
    private val restClient: RestClient
) {
    private val mutableState = MutableStateFlow<State>(State.ListAssessments(restClient))

    val state: StateFlow<State> = mutableState

    // There is room to automate this method, so that the UI doesn't have to check before making state transitions
    fun transition(isViewLastResult: Boolean = false) {
        when (val assessmentStateSnapshot = state.value) {
            is State.ListAssessments -> {
                val getListSnapshot = assessmentStateSnapshot.state.value
                if (getListSnapshot is NetworkViewModelState.Complete) {
                    val newestAssessment = getListSnapshot.result.getNewestAssessment()
                    // The server considers "completeness" based on the questions answered.  So if new questions are added,
                    // an old assessment will be considered incomplete.  isViewLastResult causes age cutoff to be ignored,
                    // requiring users to just answer the newly added questions so that their old results can be loaded
                    if (null != newestAssessment && (newestAssessment.isWithinCutoff() || isViewLastResult)) {
                        mutableState.tryEmit(State.GetMostRecentAssessmentStatus(restClient))
                    } else {
                        mutableState.tryEmit(State.CreateAssessment(restClient))
                    }
                }
            }
            is State.GetMostRecentAssessmentStatus -> {
                val getStatusSnapshot = assessmentStateSnapshot.state.value
                if (getStatusSnapshot is NetworkViewModelState.Complete) {
                    handleGetMostRecentAssessmentStatus(getStatusSnapshot, isViewLastResult)
                }
            }
            is State.CreateAssessment -> {
                val createSnapshot = assessmentStateSnapshot.state.value
                if (createSnapshot is NetworkViewModelState.Complete) {
                    mutableState.tryEmit(State.Done(createSnapshot.result, Assessment.State.DemographicsOverview))
                }
            }
            is State.Done -> {
                // Do nothing; cannot transition further
            }
        }
    }

    private fun handleGetMostRecentAssessmentStatus(
        getStatusSnapshot: NetworkViewModelState.Complete<Unit, Assessment.State, State.GetMostRecentAssessmentStatus.AssessmentWithState>,
        isViewLastResult: Boolean
    ) {
        val newState =
            when (getStatusSnapshot.result.state) {
                Assessment.State.Complete -> {
                    if (isViewLastResult) {
                        // Special case to help people get back to their last result.
                        // The wizard will know how to transition
                        State.Done(getStatusSnapshot.result.assessment, getStatusSnapshot.result.state)
                    } else {
                        State.CreateAssessment(restClient)
                    }
                }
                else -> {
                    State.Done(getStatusSnapshot.result.assessment, getStatusSnapshot.result.state)
                }
            }

        mutableState.tryEmit(newState)
    }

    companion object {
        @Suppress("MagicNumber")
        internal val OLD_ASSESSMENT_CUTOFF = 7.days
    }

    sealed class State {
        class ListAssessments(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Unit, List<Assessment>, List<Assessment>>>(NetworkViewModelState.Idle())

            val state: StateFlow<NetworkViewModelState<Unit, List<Assessment>, List<Assessment>>> = mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param loginBuilder Login to check for.
             * Note: [state] is expected to be [NetworkViewModelState.Idle] or [NetworkViewModelState.Failure] when this method is called.
             */
            fun startListCheck(
                inScope: CoroutineScope,
                authentication: Authentication
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Unit, List<Assessment>, List<Assessment>> =
                        when (val restResponse = restClient.getAssessments(authentication)) {
                            is RestResponse.Success -> NetworkViewModelState.Complete(restResponse.result)
                            is RestResponse.Failure -> NetworkViewModelState.Failure(Unit, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        class GetMostRecentAssessmentStatus(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Unit, Assessment.State, AssessmentWithState>>(
                    NetworkViewModelState.Idle()
                )

            val state: StateFlow<NetworkViewModelState<Unit, Assessment.State, AssessmentWithState>> = mutableState

            /**
             * @param inScope Scope to use for requests to the server.
             * @param authentication Authentication fo the request.
             * @param assessment Assessment whose status is to be determined.
             * Note: [state] is expected to be [NetworkViewModelState.Idle] or [NetworkViewModelState.Failure] when this method is called.
             */
            fun getAssessmentStatus(
                inScope: CoroutineScope,
                authentication: Authentication,
                assessment: Assessment
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Unit, Assessment.State, AssessmentWithState> =
                        when (val restResponse = restClient.getAssessmentStatus(authentication, assessment)) {
                            is RestResponse.Success -> {
                                NetworkViewModelState.Complete(AssessmentWithState(assessment, restResponse.result))
                            }
                            is RestResponse.Failure -> NetworkViewModelState.Failure(Unit, restResponse)
                        }

                    mutableState.tryEmit(newState)
                }
            }

            data class AssessmentWithState(val assessment: Assessment, val state: Assessment.State)
        }

        class CreateAssessment(private val restClient: RestClient) : State() {
            private val mutableState =
                MutableStateFlow<NetworkViewModelState<Assessment.Builder, Assessment, Assessment>>(
                    NetworkViewModelState.Idle()
                )

            val state: StateFlow<NetworkViewModelState<Assessment.Builder, Assessment, Assessment>> = mutableState

            /**
             * @param inScope Scope to use for requests to the sign in service.
             * @param authentication Authentication of the user who will own the assessment.
             * @param assessmentBuilder Assessment to create.
             * Note: [state] is expected to be [NetworkViewModelState.Idle] or [NetworkViewModelState.Failure] when this method is called.
             */
            fun create(
                inScope: CoroutineScope,
                authentication: Authentication,
                assessmentBuilder: Assessment.Builder
            ) {
                check(state.value is NetworkViewModelState.Idle || state.value is NetworkViewModelState.Failure)
                mutableState.tryEmit(NetworkViewModelState.Busy())

                inScope.launch {
                    val newState: NetworkViewModelState<Assessment.Builder, Assessment, Assessment> =
                        when (val assessmentResult = restClient.createAssessment(authentication, assessmentBuilder)) {
                            is RestResponse.Success -> {
                                NetworkViewModelState.Complete(assessmentResult.result)
                            }
                            is RestResponse.Failure -> {
                                NetworkViewModelState.Failure(assessmentBuilder, assessmentResult)
                            }
                        }

                    mutableState.tryEmit(newState)
                }
            }
        }

        data class Done(val assessment: Assessment, val state: Assessment.State) : State()
    }
}

private val assessmentComparator =
    Comparator<Assessment> { assessment1, assessment2 ->
        compareValuesBy(assessment1, assessment2, { it.createdAt }, { it.id })
    }

/**
 * @receiver A list of assessments
 * @return The assessment with the newest creation time.  Note that if two assessments have the same creation time, then
 * this uses the UUID as a tiebreaker so that sorts are always consistent.
 */
fun List<Assessment>.getNewestAssessment(): Assessment? = sortedWith(assessmentComparator).lastOrNull()

// In the future, it would be nice to inject a clock implementation to make testing easier
fun Assessment.isWithinCutoff() = createdAt >= Clock.System.now() - AssessmentViewModel.OLD_ASSESSMENT_CUTOFF
